/**
 * One save, one result: edit the newest document, compile, apply.
 *
 * Three screens change the team now — the editor, the board columns, the
 * operator's program — and they all follow the same path: take the newest
 * stored version's bytes, transform the part they own, draft (the compiler
 * decides, AC-001), activate, and the daemon hot-reloads before the request
 * returns. The draft/activate split still exists on the wire and in history;
 * what is gone is the ceremony that asked a person to press it in two acts
 * and then restart a container.
 */

import { useQueryClient } from '@tanstack/react-query';

import { mutationContext } from '../client/data-source.js';
import { useStore } from './store-context.js';

export type ApplyOutcome =
  | { readonly kind: 'applied'; readonly version: number }
  /** The transform produced the bytes already running. Not an error. */
  | { readonly kind: 'unchanged' };

export class CompileRefusal extends Error {
  readonly findings: readonly string[];

  constructor(findings: readonly string[]) {
    super(findings.join('\n'));
    this.name = 'CompileRefusal';
    this.findings = findings;
  }
}

/**
 * A failed save, in words a person can act on.
 *
 * Two different things go wrong here and they must not share a headline: the
 * compiler refusing the *document* (fix the fields it names), and the save
 * machinery failing (nothing wrong with the document — read what happened).
 * The second used to render as "The compiler refused this" over a raw
 * `ApiError: {"error":{...}}` blob, which blamed the person for a bug.
 */
export function describeSaveFailure(error: unknown): { readonly title: string; readonly lines: readonly string[] } {
  if (error instanceof CompileRefusal) {
    return { title: 'The compiler refused this document', lines: error.findings };
  }
  const raw = error instanceof Error ? error.message : String(error);
  // ApiError messages carry the server's JSON body; surface its sentence.
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(jsonStart)) as { error?: { message?: unknown } };
      if (typeof parsed.error?.message === 'string') {
        return { title: 'The save did not go through', lines: [parsed.error.message] };
      }
    } catch {
      // Not JSON after all; fall through to the raw text.
    }
  }
  return { title: 'The save did not go through', lines: [raw] };
}

/**
 * Returns the apply function. Not a react-query mutation, deliberately: the
 * flow is several dependent requests, and the caller owns its own progress
 * state — what it needs from here is the sequence and the cache invalidation.
 */
export function useApplyTeamDocument(): (
  transform: (source: string) => string,
  expectedBaseVersion?: number,
) => Promise<ApplyOutcome> {
  const store = useStore();
  const client = useQueryClient();

  return async (transform, expectedBaseVersion) => {
    const teamId = store.currentTeamId;
    if (teamId === null) throw new Error('no team is active');

    const versions = await store.data.teamVersions();
    const newest = versions[0];
    const active = versions.find((version) => version.state === 'ACTIVE') ?? null;
    if (newest === undefined) throw new Error('this team has no stored version to edit');

    /*
     * Transform what is *running*, not what was last stored. Activating can
     * legally re-attach to an older version (a save whose content already
     * existed), which leaves newer superseded versions above the active one —
     * and an edit based on those would silently resurrect them.
     */
    const base = active ?? newest;

    /*
     * A stale tab must not overwrite a newer team. The screen says which
     * version its form state came from; if the running version has moved on —
     * another tab saved, another person applied — this save would silently
     * reapply an old configuration wearing today's timestamp. Observed live:
     * a tab left open on version 9 re-activated it over version 10.
     */
    if (expectedBaseVersion !== undefined && base.version !== expectedBaseVersion) {
      throw new Error(
        `This screen loaded version ${String(expectedBaseVersion)}, but version ${String(base.version)} is running now. Reload the page and redo the change.`,
      );
    }

    const source = await store.data.teamVersionDocument(teamId, base.version);
    if (source === null) throw new Error('the base version stored no document source');

    const next = transform(source);

    const context = mutationContext(store.config.actorId);
    const draft = await store.data.draftTeamVersion(teamId, next, context, base.version);

    let version: number | null = draft.drafted && typeof draft.version === 'number' ? draft.version : null;
    if (version === null) {
      const already = (draft.errors ?? []).find((error) => error.rule === 'already-saved');
      if (already !== undefined) {
        /*
         * The same bytes as a stored version. As the *active* one: genuinely
         * nothing changed. As an older draft: activate it now — that is what
         * the person is asking for. A refusal that names no version at all is
         * the rarest case — two saves racing on the version number — and the
         * only honest answer is that the other one won.
         */
        if (typeof draft.version !== 'number') {
          throw new Error('Another save landed at the same moment and won. Check the result, then save again if needed.');
        }
        if (active !== null && draft.version === active.version) return { kind: 'unchanged' };
        version = draft.version;
      } else {
        throw new CompileRefusal(
          (draft.errors ?? []).map((error) => (error.path.length === 0 ? error.message : `${error.path} — ${error.message}`)),
        );
      }
    }

    await store.data.activateTeamVersion(teamId, version, {
      ...context,
      idempotencyKey: `${context.idempotencyKey}-activate`,
    });

    // Everything the new plan can change. Cheap, local, and the alternative
    // is a screen confidently rendering the team that just stopped existing.
    await Promise.all(
      [
        ['team-versions'],
        ['team-status'],
        ['team-workflow'],
        ['roles'],
        ['board'],
        ['operator-status'],
        ['integrations'],
      ].map((key) => client.invalidateQueries({ queryKey: key })),
    );

    return { kind: 'applied', version };
  };
}
